import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Books, Plus, ArrowSquareOut, PencilSimple, WarningCircle } from "@phosphor-icons/react";

const blank = { title: "", doc_type_id: "", file_url: "", revision: "" };

export default function DocumentsPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("administrator", "asset_manager", "maintenance", "supervisor");
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // doc being edited
  const [form, setForm] = useState(blank);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/config/document-types").then((r) => setTypes(r.data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/documents/${editing}`, form);
      else await api.post("/documents", form);
      toast.success(editing ? "Document updated" : "Document added");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch { toast.error("Failed"); }
  };

  const openDoc = async (d) => {
    try {
      const r = await api.get(`/documents/${d.id}/open`);
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.status === 403 ? "You don't have permission to view this document."
        : e.response?.status === 404 ? "No document file attached." : "Could not open document.");
    }
  };

  const startEdit = (d) => { setEditing(d.id); setForm({ title: d.title || "", doc_type_id: d.doc_type_id || "", file_url: d.file_url || "", revision: d.revision || "" }); setOpen(true); };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Documents &amp; Manuals</h1>
        {canManage && (
          <Button data-testid="add-document" onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={18} weight="bold" className="mr-1" />Add Document</Button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {docs.map((d) => {
          const hasFile = !!(d.file_url && d.file_url.trim());
          return (
            <div key={d.id} data-testid={`doc-${d.id}`} className="bg-white border-2 border-slate-300 rounded-lg p-4 flex flex-col gap-2">
              <button onClick={() => hasFile && openDoc(d)} disabled={!hasFile} className={`text-left ${hasFile ? "cursor-pointer" : "cursor-default"}`}>
                <Books size={24} weight="bold" className="text-slate-400 mb-2" />
                <div className="font-body font-semibold text-slate-900">{d.title}</div>
                {d.revision && <div className="font-mono text-xs text-slate-500">Rev {d.revision}</div>}
              </button>
              <div className="mt-auto flex items-center gap-2 pt-1">
                {hasFile ? (
                  <Button data-testid={`doc-open-${d.id}`} onClick={() => openDoc(d)} className="h-8 bg-blue-600 hover:bg-blue-700 font-semibold text-xs"><ArrowSquareOut size={14} weight="bold" className="mr-1" />Open Document</Button>
                ) : (
                  <span data-testid={`doc-nofile-${d.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><WarningCircle size={14} weight="bold" />No document file attached</span>
                )}
                {canManage && <Button data-testid={`doc-edit-${d.id}`} variant="outline" onClick={() => startEdit(d)} className="h-8 font-semibold text-xs ml-auto"><PencilSimple size={13} weight="bold" className="mr-1" />Edit</Button>}
              </div>
            </div>
          );
        })}
        {docs.length === 0 && <p className="text-slate-400">No documents.</p>}
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setEditing(null); } }}>
        <DialogContent className="bg-white" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Document" : "Add Document"}</DialogTitle></DialogHeader>
          <Input data-testid="doc-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <select data-testid="doc-type" className="h-11 border-2 border-slate-300 rounded px-2" value={form.doc_type_id} onChange={(e) => setForm({ ...form, doc_type_id: e.target.value })}>
            <option value="">Type…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Input data-testid="doc-url" placeholder="File URL or uploaded file path" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} />
          <Input data-testid="doc-rev" placeholder="Revision" value={form.revision} onChange={(e) => setForm({ ...form, revision: e.target.value })} />
          <Button data-testid="doc-save" onClick={save} disabled={!form.title} className="bg-blue-600 hover:bg-blue-700 font-semibold">{editing ? "Save" : "Add"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
